import React, { useState, useEffect } from 'react';

const BugHuntGame = () => {
  const [gameState, setGameState] = useState<'menu' | 'playing' | 'finished'>('menu');
  const [score, setScore] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10); // Reduced to 10 seconds
  const [selectedLines, setSelectedLines] = useState<Set<number>>(new Set());
  const [foundBugs, setFoundBugs] = useState<Set<number>>(new Set());

  // 🐛 EXPANDED BUG CHALLENGES
  const bugChallenges = [
    {
      title: "Missing Semicolon",
      language: "JavaScript",
      code: `function calculateSum(a, b) {
  let result = a + b
  return result;
}

console.log(calculateSum(5, 3));`,
      bugs: [1], // Line 2 is index 1 (0-based)
      explanation: "Missing semicolon after 'let result = a + b'"
    },
    {
      title: "Typo in Variable Name",
      language: "JavaScript", 
      code: `function greetUser(name) {
  let greeting = "Hello, " + nam + "!";
  return greeting;
}

console.log(greetUser("Alice"));`,
      bugs: [1], // Line 2 is index 1 (0-based)
      explanation: "Variable 'nam' should be 'name'"
    },
    {
      title: "Array Index Error",
      language: "JavaScript",
      code: `let numbers = [1, 2, 3, 4, 5];
let lastIndex = numbers.length;
let lastNumber = numbers[lastIndex];

console.log("Last number:", lastNumber);`,
      bugs: [2], // Line 3 is index 2 (0-based)  
      explanation: "Array index should be numbers.length - 1"
    },
    {
      title: "Infinite Loop",
      language: "JavaScript",
      code: `let count = 0;
while (count < 10) {
  console.log("Count is:", count);
  // Missing increment!
}
console.log("Done!");`,
      bugs: [2], // Line 3 is index 2 (0-based)
      explanation: "Missing count++ to increment the counter"
    },
    {
      title: "Wrong Comparison Operator",
      language: "JavaScript",
      code: `function checkAge(age) {
  if (age = 18) {
    return "Exactly 18!";
  }
  return "Not 18";
}`,
      bugs: [1], // Line 2 is index 1 (0-based)
      explanation: "Should use === for comparison, not = for assignment"
    },
    {
      title: "Missing Return Statement",
      language: "JavaScript",
      code: `function multiply(x, y) {
  let result = x * y;
  // Missing return!
}

let answer = multiply(4, 5);
console.log(answer);`,
      bugs: [1], // Line 2 is index 1 (0-based)
      explanation: "Function should return the result"
    },
    {
      title: "Undefined Variable",
      language: "JavaScript",
      code: `function displayMessage() {
  let message = "Hello World";
  console.log(mesage);
}

displayMessage();`,
      bugs: [2], // Line 3 is index 2 (0-based)
      explanation: "Variable 'mesage' should be 'message'"
    },
    {
      title: "Wrong Array Method",
      language: "JavaScript",
      code: `let fruits = ["apple", "banana", "orange"];
let firstFruit = fruits.pop();

console.log("First fruit:", firstFruit);`,
      bugs: [1], // Line 2 is index 1 (0-based)
      explanation: "Should use shift() to get first element, not pop()"
    }
  ];

  const getCurrentChallenge = () => {
    return bugChallenges[currentLevel % bugChallenges.length];
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setCurrentLevel(0);
    setTimeLeft(10); // Start with 10 seconds
    setSelectedLines(new Set());
    setFoundBugs(new Set());
  };

  const goToMenu = () => {
    setGameState('menu');
    setScore(0);
    setCurrentLevel(0);
    setTimeLeft(10);
    setSelectedLines(new Set());
    setFoundBugs(new Set());
  };

  const handleLineClick = (lineIndex: number) => {
    console.log(`Clicked line ${lineIndex + 1}`); // Debug log
    
    if (gameState !== 'playing') {
      console.log('Game not active');
      return;
    }
    
    const challenge = getCurrentChallenge();
    const newSelected = new Set(selectedLines);
    
    // Toggle line selection
    if (newSelected.has(lineIndex)) {
      newSelected.delete(lineIndex);
    } else {
      newSelected.add(lineIndex);
    }
    
    setSelectedLines(newSelected);
    
    // Check if this line has a bug
    if (challenge.bugs.includes(lineIndex)) {
      console.log('Bug found!');
      const newFoundBugs = new Set(foundBugs);
      newFoundBugs.add(lineIndex);
      setFoundBugs(newFoundBugs);
      
      // Add points for finding bug
      setScore(prev => prev + 100);
      
      // Check if all bugs found
      if (newFoundBugs.size === challenge.bugs.length) {
        console.log('Level complete!');
       setTimeout(() => {
  if (currentLevel + 1 >= bugChallenges.length) {
    setGameState('finished');
  } else {
    setCurrentLevel(prev => prev + 1);
    setSelectedLines(new Set());
    setFoundBugs(new Set());
    setTimeLeft(prev => prev + 5); // Less bonus time due to shorter timer
  }
}, 1500);

      }
    } else {
      console.log('Wrong line clicked');
      setScore(prev => Math.max(0, prev - 10)); // Less penalty
    }
  };

  // Timer effect
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setGameState('finished');
    }
  }, [gameState, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderCode = () => {
    const challenge = getCurrentChallenge();
    const lines = challenge.code.split('\n');
    
    return (
      <div className="bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 rounded-2xl p-8 font-mono text-sm border border-cyan-500/20 shadow-2xl shadow-cyan-500/10">
        <div className="flex items-center gap-3 mb-6 text-gray-300">
          <div className="flex items-center gap-2 bg-blue-500/20 px-4 py-2 rounded-full border border-blue-400/30">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
            <span className="text-blue-300 font-bold text-lg">{challenge.language}</span>
          </div>
          <div className="h-px bg-gradient-to-r from-cyan-500 to-purple-500 flex-1"></div>
        </div>
        
        <div className="space-y-1 bg-black/40 rounded-xl p-4 border border-gray-700/50">
          {lines.map((line, index) => {
            const isSelected = selectedLines.has(index);
            const isBug = challenge.bugs.includes(index);
            const isFound = foundBugs.has(index);
            
            return (
              <div
                key={index}
                className={`
                  flex items-center gap-4 p-4 rounded-xl cursor-pointer 
                  transition-all duration-300 border-2 border-transparent
                  hover:bg-gray-800/60 hover:border-cyan-400/40 hover:shadow-lg hover:shadow-cyan-400/20
                  hover:scale-[1.02] transform
                  ${isFound 
                    ? 'bg-gradient-to-r from-green-900/60 to-emerald-800/40 border-green-400 shadow-lg shadow-green-400/30 animate-pulse' 
                    : isSelected 
                    ? 'bg-gradient-to-r from-yellow-900/40 to-orange-800/20 border-yellow-400 shadow-lg shadow-yellow-400/20' 
                    : 'hover:bg-gray-800/40'
                  }
                `}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLineClick(index);
                }}
                style={{ userSelect: 'none' }}
              >
                <span className="text-gray-400 text-sm w-10 text-right font-bold bg-gray-800/50 px-2 py-1 rounded-md">
                  {index + 1}
                </span>
                <span className={`flex-1 font-medium ${isFound ? 'text-green-300' : 'text-gray-300'}`}>
                  {line || ' '}
                </span>
                {isFound && (
                  <div className="flex items-center gap-2 text-green-400 text-sm font-bold animate-bounce bg-green-500/20 px-3 py-1 rounded-full border border-green-400/50">
                    <span className="text-lg">🐛</span>
                    <span>BUG FOUND!</span>
                  </div>
                )}
                {isSelected && !isFound && (
                  <div className="flex items-center gap-2 text-yellow-400 text-sm bg-yellow-500/20 px-3 py-1 rounded-full border border-yellow-400/50">
                    <span>👆</span>
                    <span>SELECTED</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Show explanation when bug is found */}
        {foundBugs.size === challenge.bugs.length && (
          <div className="mt-6 p-6 bg-gradient-to-r from-green-900/40 to-emerald-800/30 border border-green-400/50 rounded-2xl shadow-lg shadow-green-400/20 animate-fade-in">
            <h4 className="text-green-300 font-bold mb-3 text-lg flex items-center gap-2">
              <span className="text-2xl">✅</span>
              Explanation:
            </h4>
            <p className="text-green-200 text-base leading-relaxed">{challenge.explanation}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-blue-900 to-gray-900 p-6 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-10 left-1/2 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        
        {/* MENU SCREEN */}
        {gameState === 'menu' && (
          <div className="text-center text-white">
            <div className="mb-8 animate-fade-in">
              <h1 className="text-8xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-pulse">
                🐛 Bug Hunt
              </h1>
              <div className="flex justify-center mb-4">
                <div className="h-1 w-32 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full"></div>
              </div>
            </div>
            
            <p className="text-2xl text-gray-300 mb-6 animate-fade-in delay-300">
              Find the bugs in the code. Click on lines you think have errors.
            </p>
            <p className="text-xl text-blue-300 mb-12 animate-fade-in delay-500">
              {bugChallenges.length} levels await you!
            </p>
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-10 mb-12 border border-white/10 shadow-2xl animate-fade-in delay-700">
              <h2 className="text-3xl font-bold mb-8 text-cyan-300">📋 How to Play:</h2>
              <div className="text-left space-y-4 text-xl max-w-2xl mx-auto">
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">🎯</span>
                  <span>Click on lines that contain bugs</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">✅</span>
                  <span>Correct clicks: <span className="text-green-400 font-bold">+100 points</span></span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">❌</span>
                  <span>Wrong clicks: <span className="text-red-400 font-bold">-10 points</span></span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">🎉</span>
                  <span>Find all bugs to advance to next level</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">⏰</span>
                  <span>You have <span className="text-yellow-400 font-bold">10 seconds</span> + bonus time</span>
                </div>
                <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">🚀</span>
                  <span><span className="text-purple-400 font-bold">{bugChallenges.length} levels</span> of increasing difficulty</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 text-white px-16 py-6 rounded-2xl text-2xl font-bold hover:scale-110 transition-all duration-300 shadow-2xl shadow-blue-500/40 hover:shadow-cyan-500/50 animate-fade-in delay-1000"
            >
              🎮 Start Bug Hunt!
            </button>
          </div>
        )}
        
        {/* GAME SCREEN */}
        {gameState === 'playing' && (
          <div className="text-white">
            {/* Game Header */}
            <div className="flex justify-between items-center mb-8 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <div className="flex gap-12">
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-400 mb-2 animate-pulse">{score}</div>
                  <div className="text-sm text-gray-400 uppercase tracking-wider">Score</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2">
                    {currentLevel + 1}/{bugChallenges.length}
                  </div>
                  <div className="text-sm text-gray-400 uppercase tracking-wider">Level</div>
                </div>
                <div className="text-center">
                  <div className={`text-4xl font-bold mb-2 ${timeLeft <= 5 ? 'text-red-400 animate-pulse' : 'text-yellow-400'}`}>
                    {formatTime(timeLeft)}
                  </div>
                  <div className="text-sm text-gray-400 uppercase tracking-wider">Time</div>
                </div>
              </div>
              
              <button
                onClick={goToMenu}
                className="bg-gradient-to-r from-red-500 to-pink-500 px-8 py-4 rounded-xl text-white hover:scale-105 font-bold transition-all duration-300 shadow-lg shadow-red-500/40"
              >
                🏠 Quit Game
              </button>
            </div>
            
            {/* Code Display */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 mb-6 border border-white/10 shadow-2xl">
              {renderCode()}
            </div>
            
            {/* Progress Info */}
            <div className="text-center text-gray-300 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-2xl">
              <p className="text-xl mb-4 flex items-center justify-center gap-3">
                <span className="text-3xl">🎯</span>
                Find <span className="font-bold text-yellow-400 text-2xl mx-2">{getCurrentChallenge().bugs.length}</span> bug(s) in this code!
              </p>
              <div className="flex items-center justify-center gap-4 text-2xl font-bold">
                <span>Bugs found:</span>
                <span className="text-green-400 text-3xl">{foundBugs.size}</span>
                <span className="text-gray-500">/</span>
                <span className="text-blue-400 text-3xl">{getCurrentChallenge().bugs.length}</span>
              </div>
              {foundBugs.size === getCurrentChallenge().bugs.length && (
                <p className="text-green-400 font-bold mt-4 animate-bounce text-xl flex items-center justify-center gap-2">
                  <span className="text-3xl">🎉</span>
                  Great job! Moving to next level...
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* GAME OVER SCREEN */}
        {gameState === 'finished' && (
          <div className="text-center text-white">
            <h1 className="text-6xl font-bold mb-8 bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent">🎯 Game Over!</h1>
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 mb-12 border border-white/10 shadow-2xl">
              <div className="text-8xl font-bold text-yellow-400 mb-6 animate-pulse">{score}</div>
              <div className="text-3xl mb-8 text-cyan-300">Final Score</div>
              <div className="text-xl text-gray-300 space-y-4">
                <div className="flex items-center justify-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">🏆</span>
                  <span>Levels Completed: <span className="font-bold text-blue-400 text-2xl">{currentLevel}</span></span>
                </div>
                <div className="flex items-center justify-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <span className="text-3xl">🐛</span>
                  <span>Bugs Found: <span className="font-bold text-green-400 text-2xl">{foundBugs.size}</span></span>
                </div>
              </div>
            </div>
            
            <div className="space-x-6">
              <button
                onClick={startGame}
                className="bg-gradient-to-r from-green-500 to-emerald-500 px-12 py-6 rounded-2xl text-white font-bold hover:scale-110 text-xl transition-all duration-300 shadow-2xl shadow-green-500/40"
              >
                🔄 Play Again
              </button>
              <button
                onClick={goToMenu}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 px-12 py-6 rounded-2xl text-white font-bold hover:scale-110 text-xl transition-all duration-300 shadow-2xl shadow-blue-500/40"
              >
                🏠 Main Menu
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BugHuntGame;